import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { categoriesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash2, Edit2, RefreshCw, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const COLORS = [
  '#064E3B', '#D97706', '#10B981', '#3B82F6', '#8B5CF6',
  '#EC4899', '#EF4444', '#F59E0B', '#06B6D4', '#6B7280'
];

const ICONS = [
  'tag', 'utensils', 'car', 'home', 'heart-pulse', 'gamepad-2',
  'shirt', 'graduation-cap', 'briefcase', 'trending-up', 'plus-circle',
  'minus-circle', 'shopping-cart', 'coffee', 'plane', 'gift'
];

const Categories = () => {
  const { language } = useAuth();
  const t = useTranslation(language);
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    icon: 'tag',
    color: '#064E3B'
  });

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      if (editingCat) {
        await categoriesAPI.update(editingCat.id, form);
        toast.success(t('categoryUpdated'));
      } else {
        await categoriesAPI.create(form);
        toast.success(t('categoryCreated'));
      }
      
      setDialogOpen(false);
      setEditingCat(null);
      setForm({ name: '', icon: 'tag', color: '#064E3B' });
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    
    try {
      await categoriesAPI.delete(id);
      toast.success(t('categoryDeleted'));
      fetchCategories();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (cat) => {
    setEditingCat(cat);
    setForm({
      name: cat.name,
      icon: cat.icon,
      color: cat.color
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingCat(null);
    setForm({ name: '', icon: 'tag', color: '#064E3B' });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="categories-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight">
            {t('categories')}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {categories.length} categorías
          </p>
        </div>
        <Button 
          onClick={openNewDialog}
          className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
          data-testid="new-category-btn"
        >
          <Plus size={16} className="mr-2" />
          {t('newCategory')}
        </Button>
      </div>

      {/* Categories Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-900" />
        </div>
      ) : categories.length === 0 ? (
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Tag size={32} className="mb-2" />
            <p>No hay categorías</p>
            <Button variant="link" onClick={openNewDialog} className="text-emerald-900">
              Crear primera categoría
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {categories.map(cat => (
            <Card 
              key={cat.id}
              className="border-slate-200 rounded-sm shadow-none hover:border-emerald-900/30 transition-colors"
              data-testid={`category-${cat.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-sm flex items-center justify-center"
                      style={{ backgroundColor: `${cat.color}15` }}
                    >
                      <Tag className="w-5 h-5" style={{ color: cat.color }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{cat.name}</p>
                      <p className="text-xs text-slate-400">{cat.icon}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEdit(cat)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-emerald-900"
                      data-testid={`edit-cat-${cat.id}`}
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(cat.id)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                      data-testid={`delete-cat-${cat.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Category Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingCat ? t('editCategory') : t('newCategory')}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('categoryName')} *
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                className="rounded-sm"
                placeholder="Nombre de la categoría"
                required
                data-testid="category-name-input"
              />
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                {t('color')}
              </Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, color }))}
                    className={cn(
                      "w-8 h-8 rounded-sm transition-transform",
                      form.color === color && "ring-2 ring-offset-2 ring-slate-900 scale-110"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
                Vista previa
              </p>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: `${form.color}15` }}
                >
                  <Tag className="w-5 h-5" style={{ color: form.color }} strokeWidth={1.5} />
                </div>
                <span className="font-medium text-slate-900">
                  {form.name || 'Nombre de categoría'}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="rounded-sm"
              >
                {t('cancel')}
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
                data-testid="save-category-btn"
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Categories;
